import { useMemo, useEffect, useState } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import useAuthUser from "../../hooks/useAuthUser";
import getGender from "../../utils/getGender";
import getExamVersion from "../../utils/getExamVersion";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const normalizeResults = (raw) => {
    // Handle API response format: {message: "success", data: [...], summary: [...]}
    let resultsArray = raw;

    // If raw is an object with 'data' property (API response format)
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.data) {
        resultsArray = raw.data;
    }

    // Now handle the array
    if (!Array.isArray(resultsArray)) {
        return [];
    }

    // Bank Soal system returns per-psychotest format, group by version
    const groupedByVersion = {};

    resultsArray.forEach((item) => {
        const versionId = item?.version_id;
        const versionName = item?.version_name || "Tanpa Nama";

        if (!groupedByVersion[versionId]) {
            groupedByVersion[versionId] = {
                versionId,
                versionName,
                submittedAt: item?.submitted_at || null,
                psychotests: []
            };
        }

        // Convert answers object {questionId: answer} to entries array
        const answersObj = item?.answers && typeof item.answers === "object" ? item.answers : {};
        const answerEntries = Object.entries(answersObj);

        groupedByVersion[versionId].psychotests.push({
            sessionName: `${versionName} - ${item.psychotest_name}`,
            psychotestName: item.psychotest_name,
            answeredCount: answerEntries.length,
            answers: answerEntries,
        });
    });

    return Object.values(groupedByVersion);
};

const KraeplinKolomDetail = ({ result }) => {
    const [activeLembar, setActiveLembar] = useState('all');

    const lembarGroups = useMemo(() => {
        if (!Array.isArray(result?.kolom_stats) || result.kolom_stats.length === 0) {
            return {};
        }

        const groups = {};
        result.kolom_stats.forEach((stat, idx) => {
            let l = stat.lembar;
            if (!l && stat.soal_name) {
                const m = stat.soal_name.match(/Lembar\s*(\d+)/i);
                if (m) l = parseInt(m[1], 10);
            }
            if (!l) {
                l = parseInt(stat.kolom, 10) || 1;
            }

            if (!groups[l]) {
                groups[l] = {
                    lembar: l,
                    columns: [],
                    total_terjawab: 0,
                    total_benar: 0,
                    total_salah: 0,
                    total_kosong: 0,
                    total_soal: 0,
                };
            }
            groups[l].columns.push(stat);
            groups[l].total_terjawab += stat.terjawab || 0;
            groups[l].total_benar += stat.benar || 0;
            groups[l].total_salah += stat.salah || 0;
            groups[l].total_kosong += stat.kosong || 0;
            groups[l].total_soal += stat.total_soal || 0;
        });

        Object.values(groups).forEach((g) => {
            g.ketelitian = g.total_terjawab > 0
                ? Math.round((g.total_benar / g.total_terjawab) * 100 * 10) / 10
                : 0;
            g.kecepatan_rata_rata = g.columns.length > 0
                ? Math.round((g.total_terjawab / g.columns.length) * 10) / 10
                : 0;
        });

        return groups;
    }, [result?.kolom_stats]);

    const lembarKeys = useMemo(() => {
        return Object.keys(lembarGroups).map(Number).sort((a, b) => a - b);
    }, [lembarGroups]);

    if (!Array.isArray(result?.kolom_stats) || result.kolom_stats.length === 0) {
        if (Array.isArray(result?.kecepatan_per_kolom) && result.kecepatan_per_kolom.length > 0) {
            return (
                <div className="mt-3">
                    <h6>Jawaban per Kolom (Kecepatan)</h6>
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Kolom</th>
                                    <th>Jumlah Jawaban</th>
                                </tr>
                            </thead>
                            <tbody>
                                {result.kecepatan_per_kolom.map((count, idx) => (
                                    <tr key={idx}>
                                        <td>K{idx + 1}</td>
                                        <td>{count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }
        return null;
    }

    const isMultiLembar = lembarKeys.length > 1;

    const renderTable = (columns, title = null, summary = null) => (
        <div className="card mb-3 border shadow-none" style={{ borderRadius: '8px', overflow: 'hidden' }}>
            {title && (
                <div className="card-header bg-light d-flex flex-wrap justify-content-between align-items-center py-2 px-3">
                    <span className="fw-bold text-dark">
                        <i className="fas fa-file-alt text-primary me-2"></i>
                        {title}
                    </span>
                    {summary && (
                        <div className="d-flex flex-wrap gap-2 small">
                            <span className="badge bg-primary">Terjawab: {summary.total_terjawab}/{summary.total_soal}</span>
                            <span className="badge bg-success">Benar: {summary.total_benar}</span>
                            <span className="badge bg-danger">Salah: {summary.total_salah}</span>
                            <span className="badge bg-warning text-dark">Kosong: {summary.total_kosong}</span>
                            <span className="badge bg-info text-white">Akurasi: {summary.ketelitian}%</span>
                        </div>
                    )}
                </div>
            )}
            <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                <table className="table table-sm table-bordered table-striped mb-0 text-center">
                    <thead className="table-light sticky-top">
                        <tr>
                            <th style={{ width: '90px' }}>Kolom</th>
                            <th>Terjawab</th>
                            <th>Benar</th>
                            <th>Salah</th>
                            <th>Kosong</th>
                            <th>Ketelitian</th>
                        </tr>
                    </thead>
                    <tbody>
                        {columns.map((stat, idx) => (
                            <tr key={idx}>
                                <td className="fw-bold text-primary">
                                    Kolom {stat.sub_kolom || (idx + 1)}
                                </td>
                                <td>{stat.terjawab} / {stat.total_soal}</td>
                                <td className="text-success fw-bold">{stat.benar}</td>
                                <td className="text-danger fw-bold">{stat.salah}</td>
                                <td className="text-muted">{stat.kosong}</td>
                                <td>
                                    <span className={`badge ${stat.ketelitian >= 75 ? 'bg-success' : (stat.ketelitian >= 50 ? 'bg-warning text-dark' : 'bg-secondary')}`}>
                                        {stat.ketelitian}%
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="mt-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold text-dark">
                    <i className="fas fa-table text-primary me-2"></i>
                    Detail Hasil per Kolom
                </h6>
                {isMultiLembar && (
                    <span className="badge bg-light text-secondary border">
                        Total {lembarKeys.length} Lembar ({result.kolom_stats.length} Kolom)
                    </span>
                )}
            </div>

            {isMultiLembar && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                        type="button"
                        className={`btn btn-sm ${activeLembar === 'all' ? 'btn-primary shadow-sm' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveLembar('all')}
                    >
                        <i className="fas fa-th-large me-1"></i> Semua Lembar
                    </button>
                    {lembarKeys.map((lembarNum) => {
                        const g = lembarGroups[lembarNum];
                        const isActive = activeLembar === lembarNum;
                        return (
                            <button
                                key={lembarNum}
                                type="button"
                                className={`btn btn-sm ${isActive ? 'btn-primary shadow-sm' : 'btn-outline-primary'}`}
                                onClick={() => setActiveLembar(lembarNum)}
                            >
                                <i className="fas fa-file-alt me-1"></i>
                                Lembar {lembarNum}
                                <span className={`badge ms-2 ${isActive ? 'bg-light text-primary' : 'bg-primary text-white'}`}>
                                    {g.columns.length} Kolom
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {isMultiLembar && activeLembar !== 'all' ? (
                lembarGroups[activeLembar] ? (
                    renderTable(
                        lembarGroups[activeLembar].columns,
                        `Lembar ${activeLembar} (${lembarGroups[activeLembar].columns.length} Kolom)`,
                        lembarGroups[activeLembar]
                    )
                ) : null
            ) : isMultiLembar && activeLembar === 'all' ? (
                lembarKeys.map((lembarNum) => (
                    <div key={lembarNum} className="mb-3">
                        {renderTable(
                            lembarGroups[lembarNum].columns,
                            `Lembar ${lembarNum} (${lembarGroups[lembarNum].columns.length} Kolom)`,
                            lembarGroups[lembarNum]
                        )}
                    </div>
                ))
            ) : (
                renderTable(result.kolom_stats)
            )}
        </div>
    );
};

const SpecialPotentialTestFinish = ({ data }) => {
    const { user } = useAuthUser();
    const [reviewUrl, setReviewUrl] = useState(null);

    useEffect(() => {
        if (user?.token) {
            setReviewUrl(`/review-soal?token=${user.token}`);
        }
    }, [user?.token]);

    // Debug: Log token untuk memastikan token tersedia
    useEffect(() => {

    }, [user?.token, reviewUrl]);

    const processed = useMemo(() => normalizeResults(data), [data]);

    // Extract calculations from API response
    const calculations = useMemo(() => {
        if (data && typeof data === 'object' && data.calculations) {
            return data.calculations;
        }
        return {};
    }, [data]);
    const flattened = useMemo(
        () =>
            processed.flatMap((version) =>
                version.psychotests.map((psych) => ({
                    versionName: version.versionName,
                    ...psych,
                }))
            ),
        [processed]
    );

    // Data untuk chart per paket
    const chartData = useMemo(
        () => ({
            labels: processed.map((version) => version.versionName),
            datasets: [
                {
                    label: "Total Jawaban per Paket",
                    data: processed.map((version) =>
                        version.psychotests.reduce((total, psych) => total + psych.answeredCount, 0)
                    ),
                    backgroundColor: [
                        "rgba(54, 162, 235, 0.8)",
                        "rgba(255, 99, 132, 0.8)",
                        "rgba(255, 205, 86, 0.8)",
                        "rgba(75, 192, 192, 0.8)",
                        "rgba(153, 102, 255, 0.8)",
                        "rgba(255, 159, 64, 0.8)"
                    ],
                    borderColor: [
                        "rgba(54, 162, 235, 1)",
                        "rgba(255, 99, 132, 1)",
                        "rgba(255, 205, 86, 1)",
                        "rgba(75, 192, 192, 1)",
                        "rgba(153, 102, 255, 1)",
                        "rgba(255, 159, 64, 1)"
                    ],
                    borderWidth: 2,
                    borderRadius: 10,
                },
            ],
        }),
        [processed]
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: true,
                    text: "Ringkasan Jawaban per Paket Psikotes",
                    font: {
                        size: 16,
                        weight: 'bold'
                    }
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            return `Total jawaban: ${context.parsed.y}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: {
                        maxRotation: 45,
                        minRotation: 0,
                        font: {
                            size: 12
                        }
                    },
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        stepSize: 1,
                        font: {
                            size: 12
                        }
                    },
                    grid: {
                        color: 'rgba(0, 0, 0, 0.1)'
                    }
                },
            },
        }),
        []
    );

    // Handle loading state vs empty results
    // If data is null or undefined, it's still loading - don't show error yet
    if (data === null || data === undefined) {
        return (
            <div className="container mx-auto py-5">
                <div className="row justify-content-center">
                    <div className="col-md-8 text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Memuat hasil ujian...</p>
                    </div>
                </div>
            </div>
        );
    }

    // If data exists but processed is empty, then truly no results
    if (!processed.length) {
        return (
            <div className="container mx-auto py-5">
                <div className="row justify-content-center">
                    <div className="col-md-8 text-center">
                        <div className="alert alert-warning" role="alert">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <strong>Hasil ujian tidak ditemukan untuk token ini</strong>
                        </div>
                        <img src="/assets/images/login.svg" alt="No Data" className="img-fluid mb-4" style={{ maxWidth: "300px" }} />
                        <h4 className="text-muted">Belum ada hasil ujian yang tersedia</h4>
                        <p className="text-muted">Silakan hubungi admin jika Anda merasa sudah mengerjakan ujian.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="container mx-auto row items-center" style={{ paddingTop: "50px", paddingBottom: "50px" }}>
                <div className="col-md-6 d-flex flex-column justify-content-center align-items-center">
                    <img src="/assets/images/login.svg" alt="" className="img-fluid w-100" style={{ maxWidth: "350px", objectFit: "contain" }} />
                    <div className="mt-5">
                        <p className="text-secondary" style={{ fontSize: "1.2rem" }}>Selamat anda telah menyelesaikan seluruh rangkaian ujian.</p>
                        <h3 className="mb-4" style={{ fontSize: "1.5rem", textTransform: "uppercase", lineHeight: 1.8, fontWeight: 700, }}>
                            HASIL NILAI TOTAL MASIH DI OLAH OLEH SERVER, ANDA BISA MENGHUBUNGI ADMIN DAN MENUNGGU MAKSIMAL 3X24 JAM.
                        </h3>
                    </div>
                </div>
                <div className="col-md-6 d-flex flex-column justify-content-center">
                    <div className="mt-2 border rounded p-5 bg-white">
                        <h1 className="fw-bold mb-5 text-center text-secondary" style={{ textTransform: "uppercase", fontSize: '1.5rem' }}>Ringkasan hasil ujian anda</h1>
                        <table className="table borderless" style={{ margin: "auto" }}>
                            <tbody>
                                <tr>
                                    <td>Nama</td>
                                    <td>{user?.nama_lengkap}</td>
                                </tr>
                                <tr>
                                    <td>No. Telepon</td>
                                    <td>{user?.no_tlp || '-'}</td>
                                </tr>
                                <tr>
                                    <td>Alamat email</td>
                                    <td>{user?.email || '-'}</td>
                                </tr>
                                <tr>
                                    <td>Tempat & Tanggal Lahir</td>
                                    <td>{user?.ttl || '-'}</td>
                                </tr>
                                <tr>
                                    <td>Jenis Kelamin</td>
                                    <td>{getGender(user?.jenis_kelamin) || '-'}</td>
                                </tr>
                                <tr>
                                    <td>Versi Ujian</td>
                                    <td>{getExamVersion(user?.version) || '-'}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Review Button - sama seperti di PotentialTest */}
                        {reviewUrl && (
                            <div className="text-center mt-4">
                                <a
                                    href={reviewUrl || '/review-soal'}
                                    className="btn btn-primary"
                                    style={{
                                        background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 24px',
                                        fontWeight: '600',
                                        textDecoration: 'none',
                                        color: 'white',
                                        display: 'inline-block',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 5px 15px rgba(0,0,0,0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                >
                                    <i className="fas fa-clipboard-list me-2"></i>
                                    Review Soal & Jawaban
                                </a>
                                <div className="mt-2">
                                    <small className="text-muted">
                                        Review soal dan jawaban di halaman yang sama
                                    </small>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Results Section dengan format yang sama seperti PotentialTest */}
            {data && processed.length > 0 && (
                <div className="container mx-auto" style={{ paddingTop: "20px", paddingBottom: "50px" }}>
                    <div className="row">
                        <div className="col-12">
                            <h1 className="fw-bold mb-5 text-center text-secondary" style={{ textTransform: "uppercase", fontSize: '1.5rem' }}>
                                Hasil Spesial Potential Test
                            </h1>

                            {/* Chart Summary */}
                            <div className="row mb-5">
                                <div className="col-12">
                                    <div className="border rounded p-4 bg-white shadow-sm">
                                        <h5 className="text-center text-secondary mb-4 fw-bold" style={{ textTransform: "uppercase" }}>
                                            Ringkasan Jawaban per Paket Psikotes
                                        </h5>
                                        {processed.length > 0 ? (
                                            <Bar data={chartData} options={chartOptions} />
                                        ) : (
                                            <div className="alert alert-info text-center">
                                                Belum ada jawaban yang tersimpan untuk hasil ini.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Breakdown per Psychotest */}
                            <div className="row mb-5">
                                {processed.map((version) => (
                                    <div key={version.versionId} className="col-12 mb-4">
                                        <div className="border rounded p-4 bg-white shadow-sm">
                                            <h5 className="text-primary mb-3 fw-bold">
                                                <i className="fas fa-box me-2"></i>
                                                {version.versionName}
                                            </h5>
                                            <div className="row">
                                                {version.psychotests.map((psych, idx) => (
                                                    <div key={idx} className="col-md-4 mb-3">
                                                        <div className="card border-info">
                                                            <div className="card-body">
                                                                <h6 className="card-title text-info">
                                                                    <i className="fas fa-brain me-2"></i>
                                                                    {psych.psychotestName}
                                                                </h6>
                                                                <p className="card-text mb-0">
                                                                    <span className="badge bg-info">
                                                                        {psych.answeredCount} Jawaban
                                                                    </span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>

                    {/* Hasil Calculation Section */}
                    <div className="row mt-5">
                        <div className="col-12">
                            <div className="border rounded p-4 bg-white shadow-sm">
                                <h3 className="text-center text-secondary mb-4 fw-bold" style={{ textTransform: "uppercase" }}>
                                    <i className="fas fa-calculator me-3"></i>
                                    Hasil Perhitungan & Analisis
                                </h3>

                                {Object.keys(calculations).length === 0 ? (
                                    <div className="alert alert-info text-center">
                                        <i className="fas fa-info-circle me-2"></i>
                                        <p className="mb-0">
                                            Hasil perhitungan akan ditampilkan setelah semua soal dikerjakan.
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        {Object.entries(calculations).map(([psychotestName, result]) => (
                                            <div key={psychotestName} className="mb-5">
                                                <h4 className="text-primary mb-3">
                                                    <i className="fas fa-brain me-2"></i>
                                                    {psychotestName}
                                                </h4>

                                                {/* POTENSI KERJA - Per Kolom format (1-10) */}
                                                {result && typeof result === 'object' &&
                                                    Object.keys(result).every(k => !isNaN(k) && parseInt(k) >= 1 && parseInt(k) <= 10) &&
                                                    Object.values(result).every(v => v && typeof v === 'object' && 'Benar' in v && 'Salah' in v) && (
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-hover">
                                                                <thead className="table-primary">
                                                                    <tr>
                                                                        <th className="text-center">Kolom</th>
                                                                        <th className="text-center">Benar</th>
                                                                        <th className="text-center">Salah</th>
                                                                        <th>Visualisasi</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(() => {
                                                                        const maxSoal = Math.max(...Object.values(result).map(d => (d.Benar || 0) + (d.Salah || 0)), 1);
                                                                        return Object.entries(result).map(([kolom, data]) => {
                                                                            const benar = data.Benar || 0;
                                                                            const salah = data.Salah || 0;
                                                                            const total = benar + salah;
                                                                            const widthPercent = maxSoal > 0 ? ((total / maxSoal) * 100).toFixed(1) : 0;

                                                                            return (
                                                                                <tr key={kolom}>
                                                                                    <td className="text-center fw-bold">Kolom {kolom}</td>
                                                                                    <td className="text-center">
                                                                                        <span className="badge bg-success" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                                                                                            {benar}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        <span className="badge bg-danger" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                                                                                            {salah}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>
                                                                                        <div className="progress" style={{ height: '26px', backgroundColor: '#e9ecef', borderRadius: '50px' }}>
                                                                                            <div
                                                                                                className="progress-bar bg-success fw-bold text-white shadow-sm"
                                                                                                style={{
                                                                                                    width: `${widthPercent}%`,
                                                                                                    minWidth: total > 0 ? '28px' : '0',
                                                                                                    fontSize: '0.95rem',
                                                                                                    borderRadius: '50px',
                                                                                                    display: 'flex',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center'
                                                                                                }}
                                                                                            >
                                                                                                {total}
                                                                                            </div>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </tbody>
                                                                <tfoot className="table-light">
                                                                    <tr className="fw-bold">
                                                                        <td className="text-center">TOTAL</td>
                                                                        <td className="text-center">
                                                                            <span className="badge bg-success" style={{ fontSize: '1.1rem', padding: '0.6rem 1.2rem' }}>
                                                                                {Object.values(result).reduce((sum, d) => sum + (d.Benar || 0), 0)}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <span className="badge bg-danger" style={{ fontSize: '1.1rem', padding: '0.6rem 1.2rem' }}>
                                                                                {Object.values(result).reduce((sum, d) => sum + (d.Salah || 0), 0)}
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            {(() => {
                                                                                const totalBenar = Object.values(result).reduce((sum, d) => sum + (d.Benar || 0), 0);
                                                                                const totalSalah = Object.values(result).reduce((sum, d) => sum + (d.Salah || 0), 0);
                                                                                const totalAll = totalBenar + totalSalah;
                                                                                const totalPersentase = totalAll > 0 ? ((totalBenar / totalAll) * 100).toFixed(1) : 0;
                                                                                return (
                                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                                        <strong className="text-dark" style={{ fontSize: '1.05rem' }}>
                                                                                            <i className="fas fa-clipboard-check text-primary me-1"></i> {totalAll} Total Soal
                                                                                        </strong>
                                                                                        <span className="badge bg-primary" style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}>
                                                                                            {totalPersentase}% Akurasi
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </td>
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    )}

                                                {/* Standard Scoring (IST, CFIT, dll) - Right/Wrong format - EXCLUDE PAPI, KRAEPELIN, and POTENSI KERJA */}
                                                {result && typeof result === 'object' && !result.type && !result.dimensions &&
                                                    !('Point Token' in result) && !result.total_benar && !result.kecepatan_per_kolom &&
                                                    !Object.values(result).some(v => v && typeof v === 'object' && 'Point Token' in v) &&
                                                    !(Object.keys(result).every(k => !isNaN(k) && parseInt(k) >= 1 && parseInt(k) <= 10) &&
                                                        Object.values(result).every(v => v && typeof v === 'object' && 'Benar' in v && 'Salah' in v)) && (
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered table-hover">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Kategori</th>
                                                                        <th>Jumlah Soal</th>
                                                                        <th>Benar</th>
                                                                        <th>Salah</th>
                                                                        <th>Persentase</th>
                                                                        <th>Kategori</th>
                                                                        <th>Keterangan</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {Object.entries(result).map(([kategori, data]) => {
                                                                        const persentase = data['Jumlah Soal'] > 0
                                                                            ? ((data.Benar / data['Jumlah Soal']) * 100).toFixed(1)
                                                                            : 0;

                                                                        return (
                                                                            <tr key={kategori}>
                                                                                <td className="fw-bold">{kategori}</td>
                                                                                <td>{data['Jumlah Soal'] || '-'}</td>
                                                                                <td className="text-success fw-bold">{data.Benar || 0}</td>
                                                                                <td className="text-danger">{data.Salah || 0}</td>
                                                                                <td>
                                                                                    <div className="progress" style={{ height: '20px' }}>
                                                                                        <div
                                                                                            className="progress-bar bg-success"
                                                                                            style={{ width: `${persentase}%` }}
                                                                                        >
                                                                                            {persentase}%
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    <span className={`badge ${data.Grafik === 'TS' ? 'bg-success' :
                                                                                        data.Grafik === 'T' ? 'bg-info' :
                                                                                            data.Grafik === 'S' ? 'bg-warning' :
                                                                                                data.Grafik === 'R' ? 'bg-danger' :
                                                                                                    'bg-secondary'
                                                                                        }`}>
                                                                                        {data.Grafik || '-'}
                                                                                    </span>
                                                                                </td>
                                                                                <td>
                                                                                    <span className={data.Keterangan === 'SESUAI STANDAR' ? 'text-success fw-bold' : 'text-warning'}>
                                                                                        {data.Keterangan || '-'}
                                                                                    </span>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}

                                                {/* PAPI Kostick Scoring - Format PT Karakter Pribadi (NEW) */}
                                                {result && typeof result === 'object' && !result.type && !result.dimensions && !result.total_benar &&
                                                    Object.values(result).some(v => v && typeof v === 'object' && 'Point Soal' in v && 'Point Token' in v) && (
                                                        <div className="row">
                                                            {/* Left Column: Hasil Jawab */}
                                                            <div className="col-md-6">
                                                                <h3 className="text-center mb-4 text-secondary" style={{ textTransform: 'uppercase', fontSize: '.9rem' }}>
                                                                    Hasil Jawab
                                                                </h3>
                                                                <table className="table table-bordered" style={{ margin: "auto" }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th className="text-center">Kategori Soal</th>
                                                                            <th className="text-center">Skor total benar</th>
                                                                            <th className="text-center">Skor hasil kerja</th>
                                                                            <th className="text-center">Keterangan</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {Object.entries(result).map(([key, value]) => {
                                                                            if (typeof value === 'object' && ('Point Soal' in value)) {
                                                                                return (
                                                                                    <tr key={key}>
                                                                                        <td className="text-center">{key}</td>
                                                                                        <td className="text-center">{value['Point Soal']}</td>
                                                                                        <td className="text-center">{value['Point Token'] !== undefined ? value['Point Token'] : 0}</td>
                                                                                        <td className="text-center">
                                                                                            <span className={value['Keterangan'] === 'SESUAI STANDAR' ? 'text-success fw-bold' : 'text-danger'}>
                                                                                                {value['Keterangan'] || '-'}
                                                                                            </span>
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>

                                                            {/* Right Column: Grafik Kategori */}
                                                            <div className="col-md-6">
                                                                <h3 className="text-center mb-4 text-secondary" style={{ textTransform: 'uppercase', fontSize: '.9rem' }}>
                                                                    Kategori kemampuan berdasarkan hasil jawab
                                                                </h3>
                                                                <table className="table table-bordered" style={{ margin: "auto", width: "100%" }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th rowSpan={2} className="text-center" style={{ width: "40%" }}>Kategori Soal</th>
                                                                            <th colSpan={5} className="text-center">Skor total benar</th>
                                                                        </tr>
                                                                        <tr>
                                                                            <th className="text-center" style={{ width: "12%" }}>RS</th>
                                                                            <th className="text-center" style={{ width: "12%" }}>R</th>
                                                                            <th className="text-center" style={{ width: "12%" }}>S</th>
                                                                            <th className="text-center" style={{ width: "12%" }}>T</th>
                                                                            <th className="text-center" style={{ width: "12%" }}>TS</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {Object.entries(result).map(([key, value]) => {
                                                                            if (typeof value === 'object' && value['List Point Kategori']) {
                                                                                const nilai = Object.keys(value['List Point Kategori']);
                                                                                const grafik = value['Grafik'];
                                                                                const index = nilai.indexOf(grafik);
                                                                                const barWidth = index >= 0 ? `${(index + 1) * 100}%` : '100%';

                                                                                return (
                                                                                    <tr key={key}>
                                                                                        <td className="text-center">{key}</td>
                                                                                        <td className="text-center" style={{ position: 'relative' }}>
                                                                                            <span
                                                                                                className="bg-danger"
                                                                                                style={{
                                                                                                    position: 'absolute',
                                                                                                    top: '30%',
                                                                                                    left: 0,
                                                                                                    width: barWidth,
                                                                                                    height: '18px',
                                                                                                    zIndex: 1,
                                                                                                    borderRadius: '2px'
                                                                                                }}
                                                                                            ></span>
                                                                                        </td>
                                                                                        <td className="text-center"></td>
                                                                                        <td className="text-center"></td>
                                                                                        <td className="text-center"></td>
                                                                                        <td className="text-center"></td>
                                                                                    </tr>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}

                                                {/* Legacy format fallback - shows Umum or other category names */}
                                                {result && !result['Scala'] && !result['Benar'] && ('Point Soal' in result) && (
                                                    <div className="card border-info mb-3">
                                                        <div className="card-header bg-info text-white">
                                                            <h5 className="mb-0">
                                                                <i className="fas fa-clipboard-list me-2"></i>
                                                                Hasil Perhitungan
                                                            </h5>
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row">
                                                                <div className="col-md-6">
                                                                    <div className="mb-2">
                                                                        <span className="fw-bold">Point Soal:</span>
                                                                        <span className="badge bg-secondary ms-2">{result['Point Soal']}</span>
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <span className="fw-bold">Point Token:</span>
                                                                        <span className="badge bg-primary ms-2">{result['Point Token']}</span>
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <span className="fw-bold">Jumlah Soal:</span>
                                                                        <span className="badge bg-secondary ms-2">{result['Jumlah Soal']}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <div className="mb-2">
                                                                        <span className="fw-bold">Keterangan:</span>
                                                                        <span className={`badge ms-2 ${result['Keterangan'] === 'SESUAI STANDAR' ? 'bg-success' : 'bg-warning'}`}>
                                                                            {result['Keterangan']}
                                                                        </span>
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <span className="fw-bold">Grafik:</span>
                                                                        <span className="badge bg-info ms-2">{result['Grafik']}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Dimensional Scoring (DISC, MBTI) */}
                                                {result.dimensions && (
                                                    <div className="card">
                                                        <div className="card-body">
                                                            <h5 className="card-title">
                                                                <i className="fas fa-chart-pie me-2"></i>
                                                                Tipe: <span className="badge bg-primary fs-6">{result.type}</span>
                                                            </h5>
                                                            <p className="text-muted">{result.description}</p>

                                                            <div className="row mt-3">
                                                                {Object.entries(result.dimensions).map(([dimension, score]) => (
                                                                    <div key={dimension} className="col-md-3 mb-3">
                                                                        <div className="card border-primary">
                                                                            <div className="card-body text-center">
                                                                                <h6 className="text-primary">{dimension}</h6>
                                                                                <h3 className="text-dark">{score}</h3>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Kraeplin Scoring */}
                                                {result.total_benar !== undefined && result.kecepatan_per_kolom && (
                                                    <div>

                                                        {/* Cards Kraepelin */}
                                                        <div className="card">
                                                            <div className="card-body">
                                                                <div className="row g-2">
                                                                    <div className="col-md-4 col-sm-6 mb-2">
                                                                        <div className="card bg-primary text-white">
                                                                            <div className="card-body text-center py-3">
                                                                                <h6 className="mb-1">Total Terjawab</h6>
                                                                                <h2 className="mb-0">{result.total_terjawab !== undefined ? result.total_terjawab : (result.total_benar + result.total_salah)}</h2>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4 col-sm-6 mb-2">
                                                                        <div className="card bg-success text-white">
                                                                            <div className="card-body text-center py-3">
                                                                                <h6 className="mb-1">Total Benar</h6>
                                                                                <h2 className="mb-0">{result.total_benar}</h2>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4 col-sm-6 mb-2">
                                                                        <div className="card bg-danger text-white">
                                                                            <div className="card-body text-center py-3">
                                                                                <h6 className="mb-1">Total Salah</h6>
                                                                                <h2 className="mb-0">{result.total_salah}</h2>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {result.total_kosong !== undefined && (
                                                                        <div className="col-md-4 col-sm-6 mb-2">
                                                                            <div className="card bg-warning text-dark">
                                                                                <div className="card-body text-center py-3">
                                                                                    <h6 className="mb-1">Belum Terjawab</h6>
                                                                                    <h2 className="mb-0">{result.total_kosong}</h2>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    <div className="col-md-4 col-sm-6 mb-2">
                                                                        <div className="card bg-info text-white">
                                                                            <div className="card-body text-center py-3">
                                                                                <h6 className="mb-1">Tingkat Ketelitian</h6>
                                                                                <h2 className="mb-0">{result.ketelitian !== undefined ? `${result.ketelitian}%` : `${(((result.total_benar) / Math.max(1, (result.total_benar + result.total_salah))) * 100).toFixed(1)}%`}</h2>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-md-4 col-sm-6 mb-2">
                                                                        <div className="card bg-secondary text-white">
                                                                            <div className="card-body text-center py-3">
                                                                                <h6 className="mb-1">Kecepatan Rata-rata</h6>
                                                                                <h2 className="mb-0">{result.kecepatan_rata_rata?.toFixed(1)} <small style={{ fontSize: '0.9rem' }}>/kolom</small></h2>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="mt-3">
                                                                    <h6>Konsistensi Kerja</h6>
                                                                    <div className="progress" style={{ height: '30px' }}>
                                                                        <div
                                                                            className="progress-bar bg-warning"
                                                                            style={{ width: `${result.konsistensi}%` }}
                                                                        >
                                                                            {result.konsistensi?.toFixed(1)}%
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <KraeplinKolomDetail result={result} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Drawing Manual Scoring */}
                                                {result.type === 'manual_scoring' && (
                                                    <div className="alert alert-warning">
                                                        <i className="fas fa-palette me-2"></i>
                                                        {result.message}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Review Soal dan Pembahasan - Inline dengan iframe */}
                    {reviewUrl && (
                        <div className="row mt-5">
                            <div className="col-12">
                                <div className="border rounded p-4 bg-white shadow-sm">
                                    <h3 className="text-center text-secondary mb-4 fw-bold" style={{ textTransform: "uppercase" }}>
                                        <i className="fas fa-book-open me-3"></i>
                                        Review Soal dan Pembahasan
                                    </h3>
                                    <iframe
                                        src={reviewUrl}
                                        style={{ width: '100%', minHeight: '600px', border: 'none' }}
                                        title="Review Soal"
                                    ></iframe>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default SpecialPotentialTestFinish;
